import { memo, useContext } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { Carousel, CarouselContent, CarouselItem } from "app/components/ui/Carousel/Carousel";
import AppGlobalsContext from "app/AppGlobalsContext";
import ProductCard from "app/pages/.shared/ProductCard/ProductCard";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import "./ProductsCarousel.scss";

const SHOW_SLIDES_NUMBER = {
	[RESOLUTION.XLARGE]: 3,
	[RESOLUTION.LARGE]: 3,
	[RESOLUTION.MEDIUM]: 2.3,
	[RESOLUTION.SMALL]: 1.2,
};

const CARD_GAP = 24;
const PHOTO_SIZES = "(min-width: 1024px) 325px, (min-width: 768px) 42vw, 80vw";

/**
 * @param {{
 * 	products?: Array<{ uri: string } & Record<string, unknown>>,
 * 	onItemClick?: () => void,
 * 	noSwiping?: boolean,
 * 	variant?: "flat" | "elevated" | "ghost",
 * 	showCta?: boolean,
 * 	bleed?: number | string,
 * 	"aria-labelledby"?: string,
 * }} props
 */
const ProductsCarousel = ({
	products = [],
	onItemClick = () => {},
	noSwiping,
	variant,
	showCta = true,
	bleed,
	"aria-labelledby": ariaLabelledBy,
}) => {
	const { resolution } = useContext(AppGlobalsContext);
	const { formatMessage } = useIntl();
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const slidesPerView = SHOW_SLIDES_NUMBER[resolution];
	const isSingleProductOnMobile = isMobile && products.length === 1;

	const carouselOptions = {
		loop: false,
		watchDrag: !noSwiping,
		align: isSingleProductOnMobile ? "center" : "start",
		skipSnaps: true,
	};

	return (
		<Carousel
			className="products-carousel"
			opts={carouselOptions}
			slidesPerView={slidesPerView}
			gap={CARD_GAP}
			bleed={bleed}
			aria-labelledby={ariaLabelledBy}
			aria-label={
				ariaLabelledBy ? undefined : formatMessage({ id: "listing.products.current.label" })
			}
		>
			<CarouselContent>
				{products.map(product => (
					<CarouselItem key={product.uri} className="products-carousel__slide">
						<div
							className="products-carousel__item"
							onClick={onItemClick}
							data-testid={`product-${product?.uri}`}
						>
							<ProductCard
								isMobile={isMobile}
								product={product}
								displayCardSliderOnMob={false}
								variant={variant}
								showCta={showCta}
								photoSizes={PHOTO_SIZES}
							/>
						</div>
					</CarouselItem>
				))}
			</CarouselContent>
		</Carousel>
	);
};

ProductsCarousel.propTypes = {
	products: PropTypes.arrayOf(PropTypes.object),
	onItemClick: PropTypes.func,
	noSwiping: PropTypes.bool,
	variant: PropTypes.oneOf(["flat", "elevated", "ghost"]),
	showCta: PropTypes.bool,
	bleed: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	"aria-labelledby": PropTypes.string,
};

export default memo(ProductsCarousel);
